<p-dialog
  [visible]="visible()" 
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="true"
  [closeOnEscape]="true"
  styleClass="w-full md:w-30rem"
  (onHide)="onClose()"
  [attr.aria-label]="'saved-payment-methods-dialog'"
  >
  <ng-template pTemplate="header">
    <div class="flex align-items-center gap-2">
      <span class="font-semibold text-xl">
        {{ 'POS.MEMBERSHIP_ADDON.SAVED_PAYMENT_METHODS' | translate }}
      </span>
    </div>
  </ng-template>

  <div class="flex flex-column gap-3">
    <!-- Loading State -->
    @if (loading$.value) {
      <div class="flex justify-content-center align-items-center py-5">
        <p-progressSpinner styleClass="w-4rem h-4rem"
          strokeWidth="4"
          [attr.aria-label]="'loading-spinner'"
         />
      </div>
    }

    <!-- No Payment Methods -->
    @if (!loading$.value && !hasPaymentMethods) {
      <div class="text-center py-4">
        <p-message severity="info"
          [text]="'POS.MEMBERSHIP_ADDON.NO_PAYMENT_METHODS' | translate"
          [attr.aria-label]="'no-payment-methods-message'"
         />
      </div>
    }

    <!-- Payment Methods List -->
    @if (!loading$.value && hasPaymentMethods) {
      <div class="flex flex-column gap-3">
        @for (paymentMethod of paymentMethods$.value; track paymentMethod) {
          <div
            class="border-1 surface-border border-round p-3"
            [class.surface-100]="!isPaymentMethodSelectable(paymentMethod)"
            [class.cursor-pointer]="isPaymentMethodSelectable(paymentMethod)"
            [class.cursor-not-allowed]="!isPaymentMethodSelectable(paymentMethod)"
            (click)="isPaymentMethodSelectable(paymentMethod) && onPaymentMethodSelect(paymentMethod.id)"
            [attr.aria-label]="'payment-method-' + paymentMethod.id"
            >
            <div class="flex align-items-start gap-3">
              <div class="flex align-items-center" style="min-width: 24px;">
                <p-radioButton [value]="paymentMethod.id"
                  [(ngModel)]="selectedPaymentMethodId"
                  [disabled]="!isPaymentMethodSelectable(paymentMethod)"
                  [attr.aria-label]="'payment-method-radio-' + paymentMethod.id"
                 />
              </div>
              <!-- Payment Method Icon -->
              <div class="flex align-items-center justify-content-center border-circle"
                style="width: 48px; height: 48px; min-width: 48px;"
                [ngClass]="isPaymentMethodSelectable(paymentMethod) ? 'bg-primary-50' : 'surface-100'">
                <i [class]="getPaymentMethodIcon(paymentMethod) + ' text-2xl ' + getPaymentMethodIconColor(paymentMethod)"
                [attr.aria-label]="'payment-icon-' + paymentMethod.id"></i>
              </div>
              <div class="flex-1">
                <div class="flex align-items-center gap-2 mb-1">
                  <span class="font-semibold text-lg"
                    [class.text-500]="!isPaymentMethodSelectable(paymentMethod)">
                    {{ getPaymentMethodDisplayText(paymentMethod) }}
                  </span>
                  @if (!isPaymentMethodSelectable(paymentMethod)) {
                    <i
                      class="pi pi-info-circle text-orange-500"
                      [pTooltip]="getPaymentMethodDisabledTooltip(paymentMethod)"
                      tooltipPosition="top"
                      [attr.aria-label]="'disabled-info-icon'"
                    ></i>
                  }
                </div>
                <!-- Additional Details -->
                <div class="flex flex-column gap-1">
                  @if (paymentMethod.expiry_date) {
                    <div class="text-sm text-600">
                      {{ 'POS.MEMBERSHIP_ADDON.EXPIRES' | translate }}:
                      {{ paymentMethod.expiry_date | date: 'MM/yyyy' }}
                    </div>
                  }
                  @if (paymentMethod.token_account_holder_name) {
                    <div class="text-sm text-600">
                      {{ paymentMethod.token_account_holder_name }}
                    </div>
                  }
                  @if (paymentMethod.bank_name) {
                    <div class="text-sm text-600">
                      {{ paymentMethod.bank_name }}
                    </div>
                  }
                </div>
                <!-- Payment Suitability Text -->
                @if (showSuitabilityText(paymentMethod)) {
                  <div class="text-xs mb-2"
                    [ngClass]="isPaymentMethodSelectable(paymentMethod) ? 'text-primary' : 'text-500'">
                    <i class="pi pi-info-circle mr-1"></i>
                    <span>{{ getPaymentSuitabilityText(paymentMethod) }}</span>
                  </div>
                }
                @if (isDifferentPaymentGateway(paymentMethod)) {
                  <div class="mt-2">
                    <div class="text-sm text-600">
                      <div class="font-italic mb-1">
                        ({{ 'POS.MEMBERSHIP_ADDON.DISABLED_FOR_DIFFERENT_GATEWAY' | translate }})
                      </div>
                      <div class="mb-1">
                        {{ getDifferentGatewayMessage(paymentMethod) }}
                      </div>
                    </div>
                  </div>
                } @else if (!isPaymentMethodEnabled(paymentMethod)) {
                  <div class="mt-2">
                    <div class="text-sm text-600">
                      <div class="font-italic mb-1">
                        ({{ 'POS.MEMBERSHIP_ADDON.DISABLED_FOR_DIRECT_BILLING' | translate }})
                      </div>
                      <div class="mb-1">
                        {{ 'POS.MEMBERSHIP_ADDON.NOT_AUTHORIZED_MESSAGE' | translate }}
                      </div>
                      <a href="#" class="text-primary no-underline font-semibold" (click)="$event.preventDefault()">
                        [{{ 'POS.MEMBERSHIP_ADDON.SEND_AUTHORIZATION_LINK' | translate }}]
                      </a>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    }

    <!-- No Enabled Payment Methods Warning -->
    @if (!loading$.value && hasPaymentMethods && !hasEnabledPaymentMethods) {
      <div class="mt-2">
        <p-message severity="warn"
          [text]="'POS.MEMBERSHIP_ADDON.NO_ENABLED_PAYMENT_METHODS' | translate"
          styleClass="w-full"
          [attr.aria-label]="'no-enabled-methods-warning'"
         />
      </div>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-content-end gap-2">
      <p-button [label]="'common.BUTTONS.CANCEL-BUTTON-TEXT' | translate"
        severity="secondary"
        (onClick)="onClose()"
        [attr.aria-label]="'cancel-button'"
       />
      <p-button [label]="'common.BUTTONS.BTN_CONFIRM' | translate"
        (onClick)="onConfirm()"
        [disabled]="isConfirmDisabled"
        [attr.aria-label]="'confirm-button'"
       />
    </div>
  </ng-template>
</p-dialog>

